import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { professionalClientDashboardService } from '../../../services/professionalClientDashboardService';

const MesDocuments = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('factures');
  const [documents, setDocuments] = useState({
    factures: [],
    certificats: [],
    photos: [],
    autresDocuments: []
  });

  useEffect(() => {
    loadDocuments();
  }, []);

  const loadDocuments = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await professionalClientDashboardService?.getDocuments();
      setDocuments(data);
    } catch (err) {
      console.error('Error loading documents:', err);
      setError(err?.message || 'Erreur lors du chargement des documents');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = (url, filename) => {
    const link = document.createElement('a');
    link.href = url;
    link.download = filename || 'document';
    link.target = '_blank';
    document.body?.appendChild(link);
    link?.click();
    document.body?.removeChild(link);
  };

  const getStatutBadge = (statut) => {
    const badges = {
      payee: { label: 'Payée', color: 'bg-green-100 text-green-800' },
      en_attente: { label: 'En attente', color: 'bg-yellow-100 text-yellow-800' },
      en_retard: { label: 'En retard', color: 'bg-red-100 text-red-800' },
      vise: { label: 'Visé par la douane', color: 'bg-green-100 text-green-800' },
      visé: { label: 'Visé par la douane', color: 'bg-green-100 text-green-800' },
      disponible: { label: 'Disponible', color: 'bg-green-100 text-green-800' },
      en_cours: { label: 'En cours de traitement', color: 'bg-yellow-100 text-yellow-800' },
      en_preparation: { label: 'En préparation', color: 'bg-blue-100 text-blue-800' }
    };
    const badge = badges?.[statut] || badges?.en_preparation;
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-medium ${badge?.color}`}>
        {badge?.label}
      </span>
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return format(new Date(dateStr), 'd MMM yyyy', { locale: fr });
    } catch {
      return dateStr;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement des documents...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <div className="flex items-center gap-3 mb-4">
          <Icon name="AlertCircle" size={24} className="text-red-600" />
          <h3 className="text-lg font-semibold text-red-900">Erreur</h3>
        </div>
        <p className="text-red-700 mb-4">{error}</p>
        <Button onClick={loadDocuments} variant="outline">
          Réessayer
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Mes documents</h1>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow-md border border-gray-200">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-4 px-6" aria-label="Tabs">
            <button
              onClick={() => setActiveTab('factures')}
              className={`py-4 px-3 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'factures' ? 'border-green-500 text-green-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}>
              <Icon name="FileText" size={18} className="inline mr-2" />
              Factures
            </button>
            <button
              onClick={() => setActiveTab('certificats')}
              className={`py-4 px-3 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'certificats' ? 'border-green-500 text-green-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}>
              <Icon name="Award" size={18} className="inline mr-2" />
              Certificats EX1
            </button>
            <button
              onClick={() => setActiveTab('photos')}
              className={`py-4 px-3 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'photos' ? 'border-green-500 text-green-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}>
              <Icon name="Camera" size={18} className="inline mr-2" />
              Photos d'expédition
            </button>
            <button
              onClick={() => setActiveTab('autres')}
              className={`py-4 px-3 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'autres' ? 'border-green-500 text-green-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}>
              <Icon name="Folder" size={18} className="inline mr-2" />
              Autres documents
            </button>
          </nav>
        </div>

        <div className="p-6">
          {/* Factures Tab */}
          {activeTab === 'factures' && (
            <div className="space-y-4">
              <div className="flex justify-end">
                <Button size="sm" variant="outline">
                  <Icon name="Download" size={16} className="mr-2" />
                  Télécharger toutes les factures 2026
                </Button>
              </div>
              {documents?.factures?.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Icon name="FileText" size={48} className="mx-auto mb-4 text-gray-300" />
                  <p className="text-lg font-medium">Aucune facture disponible</p>
                  <p className="text-sm mt-1">Vos factures apparaîtront ici une fois vos expéditions créées.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Numéro</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Date d'émission</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Expédition liée</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Statut</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {documents?.factures?.map((facture) => (
                        <tr key={facture?.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm font-medium text-gray-900">{facture?.numero}</td>
                          <td className="px-4 py-3 text-sm text-gray-700">{formatDate(facture?.dateEmission)}</td>
                          <td className="px-4 py-3 text-sm text-gray-700">{facture?.expeditionReference}</td>
                          <td className="px-4 py-3">{getStatutBadge(facture?.statut)}</td>
                          <td className="px-4 py-3">
                            <div className="flex items-center space-x-2">
                              {facture?.url && (
                                <>
                                  <button className="text-blue-600 hover:text-blue-800" onClick={() => window.open(facture?.url, '_blank')}>
                                    <Icon name="Eye" size={18} />
                                  </button>
                                  <button className="text-green-600 hover:text-green-800" onClick={() => handleDownload(facture?.url, facture?.numero)}>
                                    <Icon name="Download" size={18} />
                                  </button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Certificats EX1 Tab */}
          {activeTab === 'certificats' && (
            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-blue-800">
                  <Icon name="Info" size={16} className="inline mr-2" />
                  <strong>Note importante :</strong> Le certificat EX1 visé par la douane vous permet de récupérer la TVA auprès de vos fournisseurs français. Conservez précieusement ce document.
                </p>
              </div>
              {documents?.certificats?.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Icon name="Award" size={48} className="mx-auto mb-4 text-gray-300" />
                  <p className="text-lg font-medium">Aucun certificat EX1 disponible</p>
                  <p className="text-sm mt-1">Vos certificats douaniers apparaîtront ici après dédouanement.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Numéro du certificat</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Date</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Expédition liée</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Statut</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {documents?.certificats?.map((cert) => (
                        <tr key={cert?.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm font-medium text-gray-900">{cert?.numero}</td>
                          <td className="px-4 py-3 text-sm text-gray-700">{formatDate(cert?.date)}</td>
                          <td className="px-4 py-3 text-sm text-gray-700">{cert?.expeditionReference}</td>
                          <td className="px-4 py-3">{getStatutBadge(cert?.statut)}</td>
                          <td className="px-4 py-3">
                            <div className="flex items-center space-x-2">
                              {cert?.url && (
                                <>
                                  <button className="text-blue-600 hover:text-blue-800" onClick={() => window.open(cert?.url, '_blank')}>
                                    <Icon name="Eye" size={18} />
                                  </button>
                                  <button className="text-green-600 hover:text-green-800" onClick={() => handleDownload(cert?.url, cert?.numero)}>
                                    <Icon name="Download" size={18} />
                                  </button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Photos Tab */}
          {activeTab === 'photos' && (
            <div className="space-y-6">
              <div className="flex justify-end">
                <Button size="sm" variant="outline">
                  <Icon name="Download" size={16} className="mr-2" />
                  Télécharger toutes les photos
                </Button>
              </div>
              {documents?.photos?.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Icon name="Camera" size={48} className="mx-auto mb-4 text-gray-300" />
                  <p className="text-lg font-medium">Aucune photo disponible</p>
                  <p className="text-sm mt-1">Les photos de vos expéditions apparaîtront ici.</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                  {documents?.photos?.map((photo) => (
                    <div key={photo?.id} className="relative group">
                      <img
                        src={photo?.url}
                        alt={`Photo expédition ${photo?.expeditionReference}`}
                        className="w-full h-40 object-cover rounded-lg border border-gray-200 hover:shadow-lg transition-shadow cursor-pointer"
                      />
                      <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-opacity rounded-lg flex items-center justify-center">
                        <Icon name="ZoomIn" size={32} className="text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                      </div>
                      <p className="text-xs text-gray-500 mt-1 truncate">{photo?.expeditionReference}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Autres Documents Tab */}
          {activeTab === 'autres' && (
            documents?.autresDocuments?.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <Icon name="Folder" size={48} className="mx-auto mb-4 text-gray-300" />
                <p className="text-lg font-medium">Aucun document disponible</p>
                <p className="text-sm mt-1">Vos autres documents (bons de livraison, connaissements...) apparaîtront ici.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Type</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Nom du fichier</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Expédition liée</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Date</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {documents?.autresDocuments?.map((doc) => (
                      <tr key={doc?.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">{doc?.type}</td>
                        <td className="px-4 py-3 text-sm text-gray-700">{doc?.nom}</td>
                        <td className="px-4 py-3 text-sm text-gray-700">{doc?.expeditionReference}</td>
                        <td className="px-4 py-3 text-sm text-gray-700">{formatDate(doc?.date)}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center space-x-2">
                            {doc?.url && (
                              <>
                                <button className="text-blue-600 hover:text-blue-800" onClick={() => window.open(doc?.url, '_blank')}>
                                  <Icon name="Eye" size={18} />
                                </button>
                                <button className="text-green-600 hover:text-green-800" onClick={() => handleDownload(doc?.url, doc?.nom)}>
                                  <Icon name="Download" size={18} />
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
};

export default MesDocuments;